'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import Fuse from 'fuse.js'
import { Search, X } from 'lucide-react'
import { ListingCard } from '@/components/marketing/content/listing-card'
import { ListingGrid } from '@/components/marketing/content/listing-grid'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { track } from '@/lib/analytics/track'
import type { SkillCategory } from '@/content/marketing/skills'

interface SkillItem {
  slug: string
  title: string
  description: string
  category: SkillCategory
  tags: string[]
  compatibleModels: string[]
  tier?: 'certified' | 'community'
  iconEmoji?: string
  iconBgColor?: string
  forkCount?: number
  i18nTriggers?: string[]
}

const SKILLS_PER_PAGE = 9

type FilterValue = SkillCategory | 'all' | 'editors-pick'

const categories: { label: string; value: FilterValue; emoji: string }[] = [
  { label: 'All Skills', value: 'all', emoji: '✦' },
  { label: "Editor's Pick", value: 'editors-pick', emoji: '⭐' },
  { label: 'SEO & Strategy', value: 'SEO & Strategy', emoji: '🔍' },
  { label: 'CRO', value: 'CRO', emoji: '📈' },
  { label: 'Content & Copy', value: 'Content & Copy', emoji: '✍️' },
  { label: 'Paid & Measurement', value: 'Paid & Measurement', emoji: '📊' },
  { label: 'Growth & Retention', value: 'Growth & Retention', emoji: '🚀' },
  { label: 'Sales GTM', value: 'Sales GTM', emoji: '🤝' },
]

function getPageNumbers(
  current: number,
  total: number,
): (number | 'ellipsis')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }
  const pages: (number | 'ellipsis')[] = [1]
  if (current > 3) pages.push('ellipsis')
  const start = Math.max(2, current - 1)
  const end = Math.min(total - 1, current + 1)
  for (let i = start; i <= end; i++) pages.push(i)
  if (current < total - 2) pages.push('ellipsis')
  pages.push(total)
  return pages
}

export function SkillsListing({ skills }: { skills: SkillItem[] }) {
  const router = useRouter()
  const searchParams = useSearchParams()

  const activeCategory = (searchParams.get('category') || 'all') as FilterValue
  const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10))
  const [query, setQuery] = useState(searchParams.get('q') || '')

  const searchTrackTimeout = useRef<ReturnType<typeof setTimeout>>(null)
  const filteredRef = useRef<SkillItem[]>([])

  const fuse = useMemo(
    () =>
      new Fuse(skills, {
        keys: [
          { name: 'title', weight: 0.35 },
          { name: 'description', weight: 0.25 },
          { name: 'tags', weight: 0.15 },
          { name: 'i18nTriggers', weight: 0.15 },
          { name: 'category', weight: 0.1 },
        ],
        threshold: 0.35,
        ignoreLocation: true,
      }),
    [skills],
  )

  const filtered = useMemo(() => {
    let result = skills

    if (query.trim()) {
      result = fuse.search(query.trim()).map((r: { item: SkillItem }) => r.item)
    }

    if (activeCategory === 'editors-pick') {
      result = result.filter(s => s.tier === 'certified')
    } else if (activeCategory !== 'all') {
      result = result.filter(s => s.category === activeCategory)
    }

    return result
  }, [skills, fuse, query, activeCategory])
  useEffect(() => { filteredRef.current = filtered }, [filtered])

  const totalPages = Math.max(1, Math.ceil(filtered.length / SKILLS_PER_PAGE))
  const page = Math.min(currentPage, totalPages)
  const paginatedSkills = filtered.slice(
    (page - 1) * SKILLS_PER_PAGE,
    page * SKILLS_PER_PAGE,
  )
  const pageNumbers = getPageNumbers(page, totalPages)

  function buildUrl(overrides: {
    page?: number
    category?: string
    q?: string
  }) {
    const qs = new URLSearchParams()
    const p = overrides.page ?? page
    const cat = overrides.category ?? activeCategory
    const search = overrides.q ?? query

    if (p > 1) qs.set('page', String(p))
    if (cat !== 'all') qs.set('category', cat)
    if (search.trim()) qs.set('q', search.trim())

    const s = qs.toString()
    return s ? `/skills?${s}` : '/skills'
  }

  function handleSearch(value: string) {
    setQuery(value)
    // Reset to page 1 on search change, push to URL for shareability
    const qs = new URLSearchParams()
    if (activeCategory !== 'all') qs.set('category', activeCategory)
    if (value.trim()) qs.set('q', value.trim())
    const s = qs.toString()
    router.replace(s ? `/skills?${s}` : '/skills', { scroll: false })

    // Debounced search tracking
    if (searchTrackTimeout.current) clearTimeout(searchTrackTimeout.current)
    if (value.trim()) {
      searchTrackTimeout.current = setTimeout(() => {
        track('skills_search', {
          query: value.trim(),
          result_count: filteredRef.current.length,
        })
      }, 500)
    }
  }

  function handleCategoryClick(cat: FilterValue) {
    track('skills_category_filter', { category: cat })
    const qs = new URLSearchParams()
    if (cat !== 'all') qs.set('category', cat)
    if (query.trim()) qs.set('q', query.trim())
    const s = qs.toString()
    router.replace(s ? `/skills?${s}` : '/skills', { scroll: false })
  }

  return (
    <>
      {/* Category cards */}
      <div className="mt-10 flex flex-wrap gap-3">
        {categories.map(cat => {
          const isActive = cat.value === activeCategory
          return (
            <button
              key={cat.value}
              type="button"
              onClick={() => handleCategoryClick(cat.value)}
              className={`flex items-center gap-2 rounded-xl border px-5 py-3 text-sm font-medium transition-all ${
                isActive
                  ? 'border-foreground bg-foreground text-background shadow-md'
                  : 'border-border bg-background text-muted-foreground hover:border-foreground/20 hover:bg-muted hover:text-foreground'
              }`}
            >
              <span className="text-base">{cat.emoji}</span>
              {cat.label}
            </button>
          )
        })}
      </div>

      {/* Search input */}
      <div className="relative mt-8">
        <Search className="text-muted-foreground absolute top-1/2 left-4 h-4 w-4 -translate-y-1/2" />
        <input
          type="text"
          value={query}
          onChange={e => handleSearch(e.target.value)}
          placeholder="Search skills by name, description, or tag…"
          className="border-border bg-background placeholder:text-muted-foreground focus:border-foreground/30 focus:ring-foreground/10 h-11 w-full rounded-lg border pr-10 pl-11 text-sm transition-colors outline-none focus:ring-1"
        />
        {query && (
          <button
            type="button"
            onClick={() => handleSearch('')}
            className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 rounded-sm p-0.5"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Results count */}
      {query.trim() && (
        <p className="text-muted-foreground mt-4 text-sm">
          {filtered.length} {filtered.length === 1 ? 'result' : 'results'} for
          &ldquo;{query.trim()}&rdquo;
        </p>
      )}

      {/* Skills grid */}
      <div className="mt-8">
        {paginatedSkills.length > 0 ? (
          <ListingGrid>
            {paginatedSkills.map((skill, index) => (
              <ListingCard
                key={skill.slug}
                href={`/skills/${skill.slug}`}
                title={skill.title}
                description={skill.description}
                category={skill.category}
                iconEmoji={skill.iconEmoji}
                iconBgColor={skill.iconBgColor}
                onClick={() =>
                  track('skills_card_click', {
                    slug: skill.slug,
                    category: skill.category,
                    position: index + 1,
                    page,
                  })
                }
                meta={[
                  skill.forkCount
                    ? `${skill.forkCount.toLocaleString()} installs`
                    : undefined,
                  skill.compatibleModels.join(' · '),
                  skill.tier === 'certified' ? '✓ Certified' : undefined,
                ]
                  .filter(Boolean)
                  .join(' — ')}
              />
            ))}
          </ListingGrid>
        ) : (
          <div className="py-16 text-center">
            <p className="text-muted-foreground text-lg">
              No skills found.{' '}
              {(query.trim() || activeCategory !== 'all') && (
                <button
                  type="button"
                  onClick={() => {
                    setQuery('')
                    handleCategoryClick('all')
                  }}
                  className="text-foreground underline underline-offset-4 hover:no-underline"
                >
                  Clear filters
                </button>
              )}
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-12">
          <Pagination>
            <PaginationContent>
              {page > 1 && (
                <PaginationItem>
                  <PaginationPrevious
                    href={buildUrl({ page: page - 1 })}
                    onClick={e => {
                      e.preventDefault()
                      track('skills_paginate', {
                        page: page - 1,
                        category: activeCategory,
                        query: query.trim() || undefined,
                      })
                      router.replace(buildUrl({ page: page - 1 }), {
                        scroll: false,
                      })
                    }}
                  />
                </PaginationItem>
              )}
              {pageNumbers.map((p, i) =>
                p === 'ellipsis' ? (
                  <PaginationItem key={`ellipsis-${i}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={p}>
                    <PaginationLink
                      href={buildUrl({ page: p })}
                      isActive={p === page}
                      onClick={e => {
                        e.preventDefault()
                        track('skills_paginate', {
                          page: p,
                          category: activeCategory,
                          query: query.trim() || undefined,
                        })
                        router.replace(buildUrl({ page: p }), { scroll: false })
                      }}
                    >
                      {p}
                    </PaginationLink>
                  </PaginationItem>
                ),
              )}
              {page < totalPages && (
                <PaginationItem>
                  <PaginationNext
                    href={buildUrl({ page: page + 1 })}
                    onClick={e => {
                      e.preventDefault()
                      track('skills_paginate', {
                        page: page + 1,
                        category: activeCategory,
                        query: query.trim() || undefined,
                      })
                      router.replace(buildUrl({ page: page + 1 }), {
                        scroll: false,
                      })
                    }}
                  />
                </PaginationItem>
              )}
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </>
  )
}
